/**
 * The conversation on a ticket, read from either chair.
 *
 * The reporter's page and the admin page render the same thread with different
 * file routes and different removal rights, so the differences arrive as props and
 * the layout is written once.
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { TicketAttachment, TicketMessage } from '@/api/support'
import { Markdown } from '@/lib/markdown'
import { formatDateTime, formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

import { AttachmentList } from './AttachmentList'

export interface ThreadTicket {
  body: string
  created_at: string
  attachments: TicketAttachment[]
  messages: TicketMessage[]
}

export function TicketThread({
  ticket,
  reporterName,
  filePath,
  canRemove,
  onRemove,
}: {
  ticket: ThreadTicket
  reporterName: string
  filePath: (attachment: TicketAttachment, inline: boolean) => string
  canRemove?: (attachment: TicketAttachment) => boolean
  onRemove?: (attachment: TicketAttachment) => void
}) {
  const { t, i18n } = useTranslation()
  const removal = (attachment: TicketAttachment) =>
    onRemove && canRemove?.(attachment) ? onRemove : undefined

  return (
    <ol className="space-y-3" aria-label={t('support.thread.title')}>
      <Message
        author={reporterName}
        staff={false}
        createdAt={ticket.created_at}
        locale={i18n.language}
        body={ticket.body}
      >
        <AttachmentList
          attachments={ticket.attachments}
          filePath={filePath}
          onRemove={ticket.attachments.some((a) => removal(a)) ? onRemove : undefined}
          className="mt-3"
        />
      </Message>

      {ticket.messages.map((message) => (
        <Message
          key={message.id}
          author={
            message.author_name ?? (message.is_staff ? t('support.thread.staff') : reporterName)
          }
          staff={message.is_staff}
          createdAt={message.created_at}
          locale={i18n.language}
          body={message.body}
        >
          <AttachmentList
            attachments={message.attachments}
            filePath={filePath}
            onRemove={message.attachments.some((a) => removal(a)) ? onRemove : undefined}
            className="mt-3"
          />
        </Message>
      ))}
    </ol>
  )
}

function Message({
  author,
  staff,
  createdAt,
  locale,
  body,
  children,
}: {
  author: string
  staff: boolean
  createdAt: string
  locale: string
  body: string
  children?: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <li
      className={cn(
        'rounded-lg border border-border bg-surface p-3 sm:p-4',
        staff && 'border-s-4 border-s-accent',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
        <span className="font-medium text-text">{author}</span>
        {staff && (
          <span className="rounded-full bg-accent-subtle px-2 py-0.5 text-[11px] font-medium text-accent">
            {t('support.thread.staff')}
          </span>
        )}
        <time dateTime={createdAt} className="ms-auto">
          {formatRelative(createdAt, locale)}
          <span className="sr-only"> ({formatDateTime(createdAt, locale)})</span>
        </time>
      </div>
      <Markdown text={body} />
      {children}
    </li>
  )
}
